import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { surface, tone } from '../timeclock/tcFormat';
import { ATTACHMENT_ACCEPT, hours, kindColor, kindLabel, locale, period, readAttachment, specialLabel, teamError, teamPost } from './teamFormat';
import { Deadlines, KindChip, StaffSelect, StatCard, StatusText } from './TeamParts';

const MONTHS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

/**
 * "Mijn verlof" (for management and Els "Verlof per persoon"): the balance,
 * the deadlines of the guidelines, the year laid out like the Excel sheet
 * (0,4 = recup earned on a worked day, month totals below) and the requests,
 * which can be cancelled while that is still allowed.
 */
const TeamMineTab = ({ t, lang, theme, token, me, staff, reloadKey, year, notify, failed, onChanged }) => {
    const [who, setWho] = useState(me.staff.id);
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [again, setAgain] = useState(0);
    const [cancelling, setCancelling] = useState(null);
    const [attachFor, setAttachFor] = useState(null);
    const attachInput = React.useRef(null);

    useEffect(() => {
        let alive = true;
        setBusy(true);
        teamPost('yearDetail', token, { staff_id: who, year }).then((response) => {
            if (!alive) return;
            setBusy(false);
            if (response?.success) {
                setData(response.data);
                setError('');
            } else {
                setError(failed(response));
            }
        });
        return () => { alive = false; };
    }, [token, who, year, reloadKey, again, failed]);

    const cancel = (r) => {
        if (!window.confirm(t('team_cancel_confirm', 'Cancel this request?') + '\n' + kindLabel(t, r.kind) + ' · ' + period(r.from, r.to, lang))) return;
        setCancelling(r.id);
        teamPost('cancelRequest', token, { id: r.id }).then((response) => {
            setCancelling(null);
            if (response?.success) {
                notify(t('team_cancelled', 'Cancelled'), true);
                setAgain((k) => k + 1);
                if (onChanged) onChanged();
            } else {
                notify(teamError(t, response), false);
            }
        });
    };

    // Send an attachment afterwards (doctor's certificate, ...) for own sickness or special leave.
    const pickAttachment = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        const r = attachFor;
        if (!file || !r) return;
        readAttachment(file).then((att) => {
            teamPost('sendAttachment', token, { id: r.id, attachment: att }).then((response) => {
                setAttachFor(null);
                if (response?.success) notify(t('team_attach_sent', 'Attachment sent to management.'), true);
                else notify(teamError(t, response), false);
            });
        }).catch((err) => {
            setAttachFor(null);
            notify(err.message === 'size' ? t('team_err_attachment_size', 'Attachment: at most 5 MB.') : t('errorOccured', 'Something went wrong'), false);
        });
    };

    const s = surface(theme);
    const dark = theme === 'dark';
    const b = data?.balance;
    const own = who === me.staff.id;
    const month = new Date().getMonth() + 1;
    const recupTone = (v) => (v < 0 ? (year === new Date().getFullYear() && month >= 11 ? tone.warn : tone.bad) : undefined);

    // Year grid like the sheet.
    const byDay = {};
    (data?.days || []).forEach((d) => { byDay[d.day] = d; });
    const sums = MONTHS.map(() => 0);
    (data?.days || []).forEach((d) => { if (d.recup) sums[parseInt(d.day.substring(5, 7), 10) - 1] += d.recup; });
    const grey = dark ? 'rgba(255,255,255,0.10)' : '#DDE2E8';
    const hatch = 'repeating-linear-gradient(135deg, transparent 0 3px, ' + (dark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)') + ' 3px 4px)';
    const fmt = (v) => (Math.round(v * 10) / 10).toFixed(1).replace('.', lang === 'en' ? '.' : ',');
    const loc = locale(lang);

    // Recup also in days: hours / a full working day of this person's roster (the Excel divides by 8,4).
    const person = staff.find((p) => p.id === who);
    const fullDay = (() => {
        const rows = person?.roster || [];
        const day = data?.today || '9999-12-31';
        const latest = rows.reduce((m, r) => (r.valid_from <= day && r.valid_from > m ? r.valid_from : m), '');
        const hoursList = rows.filter((r) => r.valid_from === latest).map((r) => r.hours);
        return hoursList.length ? Math.max(...hoursList) : 0;
    })();
    const withDays = (h) => (
        <>
            {hours(h, lang)}
            {fullDay > 0 && (
                <Box component="span" sx={{ fontSize: 16, fontWeight: 600, color: 'text.secondary', ml: 1 }}>
                    ({t('team_days_approx', { defaultValue: '≈ {{n}} days', n: fmt(h / fullDay) })})
                </Box>
            )}
        </>
    );

    const gridCell = (m, dayNo) => {
        const date = new Date(year, m, dayNo, 12);
        if (date.getMonth() !== m) return <Box component="td" key={m} sx={{ backgroundColor: dark ? 'rgba(0,0,0,0.2)' : '#F4F5F7' }} />;
        const key = year + '-' + String(m + 1).padStart(2, '0') + '-' + String(dayNo).padStart(2, '0');
        const d = byDay[key];
        if (!d) return <Box component="td" key={m} />;
        let bg = 'transparent';
        let color = 'text.primary';
        let text = '';
        let title = date.toLocaleDateString(loc, { weekday: 'long', day: 'numeric', month: 'long' }) + ': ';
        if (d.type === 'W') { bg = grey; title += t('team_weekend', 'weekend'); }
        else if (d.type === 'H') { bg = grey; title += d.name; }
        else if (d.type === 'O') { bg = hatch; title += t('team_legend_off', 'Day off by roster'); }
        else if (d.type === 'D') { text = d.recup ? fmt(d.recup) : ''; color = 'text.secondary'; title += t('team_worked', 'worked') + (d.recup ? ', +' + hours(d.recup, lang) + ' recup' : ''); }
        else {
            const c = kindColor(d.type, theme);
            bg = d.status === 'PENDING' ? 'repeating-linear-gradient(135deg, ' + c + '66 0 3px, transparent 3px 6px)' : c;
            color = d.status === 'PENDING' ? 'text.primary' : (d.type === 'REPLACEMENT' ? '#1a1a1a' : '#fff');
            if (d.type === 'RECUP') text = fmt(-d.hours);
            title += (d.type === 'SPECIAL' && d.special_code ? specialLabel(t, d.special_code) : kindLabel(t, d.type))
                + (d.hours ? ' · ' + hours(d.hours, lang) : '') + (d.status === 'PENDING' ? ' · ' + t('team_status_pending', 'requested') : '');
        }
        return (
            <Tooltip key={m} title={title} disableInteractive>
                <Box component="td" sx={{ background: bg, color, fontSize: 11, textAlign: 'center', height: 20, p: 0, fontVariantNumeric: 'tabular-nums',
                    outline: key === data.today ? '2px solid ' + tone.info : 'none', outlineOffset: -2, borderTop: s.line, borderLeft: s.line }}>
                    {text}
                </Box>
            </Tooltip>
        );
    };

    return (
        <Box>
            {me.rights.sees_all && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                    <StaffSelect staff={staff} value={who} onChange={setWho} t={t} />
                    <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{t('team_mine_all', 'Management and Els see everyone.')}</Typography>
                </Box>
            )}
            <input ref={attachInput} type="file" accept={ATTACHMENT_ACCEPT} style={{ display: 'none' }} onChange={pickAttachment}
                onClick={(e) => { e.target.value = ''; }} />
            {error && <Typography sx={{ color: tone.bad, fontSize: 13.5, mb: 1 }}>{error}</Typography>}
            {busy && !data && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>}

            {data && b && (
                <>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
                        <StatCard theme={theme} title={t('team_card_leave', 'Leave') + ' ' + year + (own ? '' : ' · ' + data.staff.name)}
                            main={b.leave.left === null ? '—' : hours(b.leave.left, lang)} mainColor={b.leave.left < 0 ? tone.bad : undefined}
                            bar={b.leave.right ? (b.leave.used + b.leave.pending) / b.leave.right * 100 : undefined}
                            lines={[
                                [t('team_leave_right', 'Right'), b.leave.right === null ? t('team_not_entered', 'not entered yet') : hours(b.leave.right, lang)],
                                ...(b.leave.adjust ? [[t('team_adjust', 'Correction'), hours(b.leave.adjust, lang)]] : []),
                                [t('team_leave_used', 'Planned and approved'), hours(b.leave.used, lang)],
                                [t('team_leave_pending', 'Requested'), hours(b.leave.pending, lang)],
                            ]} />
                        <StatCard theme={theme} title={t('team_card_recup_today', 'Recup today')}
                            main={withDays(b.recup.available)} mainColor={recupTone(b.recup.available)}
                            lines={[
                                [t('team_recup_earned', 'Earned until today'), hours(b.recup.earned_to_date, lang)],
                                [t('team_recup_taken_to_date', 'Taken until today'), hours(b.recup.taken_to_date, lang)],
                                [t('team_recup_planned', 'Planned after today'), hours(b.recup.planned, lang)],
                                [t('team_recup_after_planning', 'Left after your planning'), hours(b.recup.now, lang)],
                            ]} />
                        <StatCard theme={theme} title={t('team_card_recup_end', 'Recup on 31 December')}
                            main={withDays(b.recup.year_end)} mainColor={b.recup.year_end > 0 ? tone.warn : recupTone(b.recup.year_end)}
                            lines={[
                                [t('team_recup_year', 'Earned over the year'), hours(b.recup.earned_year, lang)],
                                [t('team_recup_expires', 'Not planned yet; expires on 31/12'), ''],
                            ]} />
                        <StatCard theme={theme} title={t('team_card_replacement', 'Replacement days')}
                            main={String(b.replacement.left)} mainColor={b.replacement.left < 0 ? tone.bad : undefined}
                            lines={[
                                [t('team_replacement_right', 'Holidays on a weekend'), String(b.replacement.right)],
                                [t('team_replacement_used', 'Planned'), String(b.replacement.used)],
                                [t('team_sick_days', 'Sick days'), String(b.sick_days)],
                            ]} />
                    </Box>

                    <Typography sx={{ fontSize: 14.5, fontWeight: 700, mb: 1 }}>{t('team_deadlines', 'Deadlines from the guidelines')}</Typography>
                    <Box sx={{ ...s.card, p: 2, mb: 2.5 }}>
                        <Deadlines list={data.deadlines} t={t} lang={lang} />
                    </Box>

                    <Typography sx={{ fontSize: 14.5, fontWeight: 700, mb: 1 }}>{t('team_requests', 'Requests and entries')}</Typography>
                    <Box sx={{ ...s.card, mb: 2.5, overflowX: 'auto' }}>
                        {data.requests.length === 0 && <Typography sx={{ p: 2, fontSize: 13.5, color: 'text.secondary' }}>{t('team_no_requests', 'Nothing yet this year.')}</Typography>}
                        {data.requests.length > 0 && (
                            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
                                <tbody>
                                    {data.requests.map((r) => (
                                        <tr key={r.id}>
                                            <Box component="td" sx={{ px: 2, py: 1, borderTop: s.line, whiteSpace: 'nowrap' }}>
                                                <KindChip kind={r.kind} t={t} theme={theme} label={r.kind === 'SPECIAL' && r.special_code ? specialLabel(t, r.special_code) : undefined} />
                                            </Box>
                                            <Box component="td" sx={{ px: 1.5, py: 1, borderTop: s.line, whiteSpace: 'nowrap' }}>{period(r.from, r.to, lang)}</Box>
                                            <Box component="td" sx={{ px: 1.5, py: 1, borderTop: s.line, whiteSpace: 'nowrap', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                                {r.days_count ? r.days_count + ' ' + (r.days_count === 1 ? t('team_day', 'day') : t('team_days', 'days')) + (r.hours ? ' · ' + hours(r.hours, lang) : '') : ''}
                                            </Box>
                                            <Box component="td" sx={{ px: 1.5, py: 1, borderTop: s.line }}><StatusText status={r.status} t={t} /></Box>
                                            <Box component="td" sx={{ px: 1.5, py: 1, borderTop: s.line, color: 'text.secondary', minWidth: 160 }}>
                                                {[r.remark, r.override_reason ? t('team_exception', 'exception') + ': ' + r.override_reason : null,
                                                    r.decision_note, r.source === 'MANAGEMENT' ? t('team_by_management', 'entered by management') : null,
                                                    r.source === 'IMPORT' ? t('team_from_excel', 'from the Excel') : null].filter(Boolean).join(' · ')}
                                            </Box>
                                            <Box component="td" sx={{ px: 2, py: 1, borderTop: s.line, textAlign: 'right' }}>
                                                {own && (r.kind === 'SICK' || r.kind === 'SPECIAL') && (r.status === 'PENDING' || r.status === 'APPROVED') && (
                                                    <Button size="small" disabled={attachFor !== null} onClick={() => { setAttachFor(r); attachInput.current?.click(); }} sx={{ textTransform: 'none' }}>
                                                        {t('team_attach_send', 'Send attachment')}
                                                    </Button>
                                                )}
                                                {r.cancellable && (
                                                    <Button size="small" color="error" disabled={cancelling === r.id} onClick={() => cancel(r)} sx={{ textTransform: 'none' }}>
                                                        {t('team_cancel', 'Cancel')}
                                                    </Button>
                                                )}
                                            </Box>
                                        </tr>
                                    ))}
                                </tbody>
                            </Box>
                        )}
                    </Box>

                    <Typography sx={{ fontSize: 14.5, fontWeight: 700, mb: 1 }}>{t('team_year_grid', 'The year')} {year}</Typography>
                    <Box sx={{ ...s.card, overflowX: 'auto' }}>
                        <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', minWidth: 720, tableLayout: 'fixed' }}>
                            <thead>
                                <tr>
                                    <Box component="th" sx={{ width: 34 }} />
                                    {MONTHS.map((m) => (
                                        <Box component="th" key={m} sx={{ fontSize: 11.5, fontWeight: 700, color: s.faint, py: 0.75, textTransform: 'uppercase' }}>
                                            {new Date(year, m, 1).toLocaleDateString(loc, { month: 'short' }).replace('.', '')}
                                        </Box>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {Array.from({ length: 31 }, (_, i) => i + 1).map((dayNo) => (
                                    <tr key={dayNo}>
                                        <Box component="td" sx={{ fontSize: 11, color: s.faint, textAlign: 'right', pr: 1, borderTop: s.line }}>{dayNo}</Box>
                                        {MONTHS.map((m) => gridCell(m, dayNo))}
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <Box component="td" sx={{ fontSize: 11.5, fontWeight: 700, textAlign: 'right', pr: 1, borderTop: s.line }}>Σ</Box>
                                    {sums.map((v, m) => (
                                        <Box component="td" key={m} sx={{ fontSize: 11.5, fontWeight: 700, textAlign: 'center', py: 0.5, borderTop: s.line,
                                            color: v < 0 ? tone.bad : 'text.primary', fontVariantNumeric: 'tabular-nums' }}>{fmt(v)}</Box>
                                    ))}
                                </tr>
                            </tfoot>
                        </Box>
                    </Box>
                    <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 1 }}>
                        {t('team_year_note', 'Same grid as the Excel: 0,4 = recup earned on a worked day, a negative number = recup taken, the month total below.')}
                    </Typography>
                </>
            )}
        </Box>
    );
};

export default TeamMineTab;
